import { type DocMeta, DocMetaSchema, type TiptapDoc } from "@klarity/schema";
import type { ThemeTokens } from "@klarity/themes";
import { useQueryClient } from "@tanstack/react-query";
import { useBlocker } from "@tanstack/react-router";
import type { Editor } from "@tiptap/core";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useConfirm } from "../../components/ui/ConfirmDialog";
import { toast } from "../../components/ui/Toast";
import { api } from "../../lib/api/endpoints";
import type { DocumentFull } from "../../lib/api/models";
import { cleanMeta, validateMeta } from "../../lib/meta";
import { ThemeCanvas } from "../canvas/ThemeCanvas";
import { KlarityEditor } from "../editor/KlarityEditor";
import { ConflictDialog } from "./ConflictDialog";
import { type Draft, type DraftStore, defaultDraftStore, sameContent } from "./drafts";
import { LockNotice } from "./LockNotice";
import { RecoveryPrompt } from "./RecoveryPrompt";
import { type AutosaveStatus, type SaveFn, type Snapshot, useAutosave } from "./useAutosave";
import { useDocumentHost } from "./useDocumentHost";
import { HEARTBEAT_MS, type LockState, useSoftLock } from "./useSoftLock";

export type EditSessionApi = {
  title: string;
  setTitle: (t: string) => void;
  meta: DocMeta;
  setMeta: (m: DocMeta) => void;
  status: AutosaveStatus;
  error: string | null;
  lastSavedAt: string | null;
  editor: Editor | null;
  editable: boolean;
  notices: ReactNode;
  canvas: ReactNode;
  snapshot: () => Snapshot;
  /** Changes the server does not have yet (not sent, in flight, or refused). */
  pending: () => boolean;
  flush: () => Promise<void>;
  done: () => Promise<void>;
};

const saveDocument: SaveFn = (docId, version, s) =>
  api.documents.save(docId, { version, title: s.title, meta: s.meta, content: s.content });

export function EditSession({
  doc,
  tokens,
  onDone,
  drafts: draftsProp,
  timings,
  children,
}: {
  doc: DocumentFull;
  tokens: ThemeTokens;
  onDone: () => void;
  drafts?: DraftStore;
  timings?: { autosaveMs?: number; heartbeatMs?: number; retryMs?: number };
  children: (s: EditSessionApi) => ReactNode;
}) {
  const drafts = useMemo(() => draftsProp ?? defaultDraftStore(), [draftsProp]);
  const queryClient = useQueryClient();
  const { confirm, element: confirmDialog } = useConfirm();
  const [title, setTitleState] = useState(doc.title);
  const [meta, setMetaState] = useState<DocMeta>(doc.meta);
  const snapshot = useRef<Snapshot>({ title: doc.title, meta: doc.meta, content: doc.content });
  const [made, setMade] = useState<{ editor: Editor; mode: "read" | "edit" } | null>(null);
  const editor = made?.editor ?? null;
  const [recovery, setRecovery] = useState<Draft | null>(null);
  const [conflict, setConflict] = useState<DocumentFull | "loading" | "failed" | null>(null);
  const [busy, setBusy] = useState(false);
  const draftChecked = useRef(false);

  // Declared before useSoftLock: listeners and cleanups run in declaration order, so the draft is on the device
  // before the lock is released on pagehide / unmount.
  const writeDraftRef = useRef<() => void>(() => {});
  useEffect(() => {
    const onPageHide = () => writeDraftRef.current();
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      writeDraftRef.current(); // Edit -> Read, navigation or any other unmount
    };
  }, []);

  const lock = useSoftLock(doc.id, api.locks, timings?.heartbeatMs);
  const held = lock.state.status === "held";
  const autosave = useAutosave({
    docId: doc.id,
    initialVersion: doc.version,
    save: saveDocument,
    getSnapshot: () => snapshot.current,
    drafts,
    enabled: held,
    delayMs: timings?.autosaveMs,
    retryMs: timings?.retryMs,
  });
  const { host, dialogs } = useDocumentHost(doc.id, tokens, meta);
  const lockLost = lock.state.status === "lost" || autosave.status === "locked";
  const editable = held && !lockLost;
  // The editor mounts in edit mode once the lock is first held and stays mounted: a lost lock only switches it
  // to read-only (setEditable(false)), so nothing typed disappears. A document never locked stays in read mode.
  const [mode, setMode] = useState<"read" | "edit">("read");
  useEffect(() => {
    if (held) setMode("edit");
  }, [held]);
  useEffect(() => {
    if (mode !== "edit" || made?.mode !== "edit" || !editor || editor.isDestroyed) return;
    editor.setEditable(editable, false);
    // setEditable(…, false) emits no transaction, so useEditorState consumers (FormatBar, node views) would stay
    // stale. A meta-only transaction re-runs them; it changes no document, history or dirty state.
    editor.view.dispatch(editor.state.tr.setMeta("addToHistory", false));
  }, [mode, made, editor, editable]);

  // Unsaved changes go to the local draft synchronously, BEFORE any flush or release: a save in flight when the
  // page goes away may still fail.
  const writeDraftNow = () => {
    if (!autosave.pending()) return;
    const s = snapshot.current;
    void drafts
      .put({ docId: doc.id, baseVersion: autosave.version(), ...s, savedAt: new Date().toISOString() })
      .catch(() => {});
  };
  writeDraftRef.current = writeDraftNow;
  const wasLost = useRef(false);
  useEffect(() => {
    if (lockLost && !wasLost.current) writeDraftRef.current();
    wasLost.current = lockLost;
  }, [lockLost]);

  const { acquire } = lock;
  useEffect(() => {
    void acquire();
  }, [acquire]);

  // Once the lock is ours: offer a local draft back if it differs from the server copy.
  useEffect(() => {
    if (!held || draftChecked.current) return;
    draftChecked.current = true;
    void drafts.get(doc.id).then((d) => {
      if (!d) return;
      const differs = !sameContent(d.content, doc.content) || d.title !== doc.title || !sameContent(d.meta, doc.meta);
      if (differs) setRecovery(d);
      else void drafts.delete(doc.id);
    });
  }, [held, drafts, doc]);

  // A 409 Conflict: fetch the latest version for the dialog.
  useEffect(() => {
    if (autosave.status !== "conflict" || conflict !== null) return;
    setConflict("loading");
    api.documents.get(doc.id).then(setConflict, () => setConflict("failed"));
  }, [autosave.status, conflict, doc.id]);

  const setTitle = useCallback(
    (t: string) => {
      setTitleState(t);
      snapshot.current = { ...snapshot.current, title: t };
      autosave.markDirty();
    },
    [autosave],
  );
  const setMeta = useCallback(
    (m: DocMeta) => {
      setMetaState(m);
      // Save only meta the server accepts: blank fields dropped, kind rules and the strict schema met.
      const clean = cleanMeta(m);
      if (Object.keys(validateMeta(doc.kind, clean)).length > 0 || !DocMetaSchema.safeParse(clean).success) return;
      snapshot.current = { ...snapshot.current, meta: clean };
      autosave.markDirty();
    },
    [autosave, doc.kind],
  );
  const loadServerCopy = (d: DocumentFull) => {
    if (editor && !editor.isDestroyed) editor.commands.setContent(d.content, { emitUpdate: false });
    snapshot.current = { title: d.title, meta: d.meta, content: d.content };
    setTitleState(d.title);
    setMetaState(d.meta);
    autosave.setVersion(d.version);
    autosave.markClean();
    void drafts.delete(doc.id);
  };

  const keepMine = async () => {
    if (!conflict || conflict === "loading" || conflict === "failed") return;
    autosave.setVersion(conflict.version);
    autosave.unblock();
    setConflict(null);
    await autosave.flush();
  };
  const takeTheirs = () => {
    if (!conflict || conflict === "loading" || conflict === "failed") return;
    loadServerCopy(conflict);
    setConflict(null);
  };
  const copyMine = async () => {
    if (!conflict || conflict === "loading" || conflict === "failed") return;
    setBusy(true);
    try {
      const mine = snapshot.current;
      const copyTitle = `${mine.title} (my copy)`;
      await api.documents.create({
        spaceId: doc.spaceId,
        parentId: doc.parentId,
        kind: "page",
        title: copyTitle,
        meta: {},
        content: mine.content,
      });
      toast(`Your version is saved as the page "${copyTitle}".`, "success");
      void queryClient.invalidateQueries({ queryKey: ["tree"] });
      loadServerCopy(conflict);
      setConflict(null);
    } catch (e) {
      toast(`The copy could not be created: ${e instanceof Error ? e.message : "unknown error"}`, "error");
    } finally {
      setBusy(false);
    }
  };

  const restoreDraft = () => {
    if (!recovery) return;
    if (editor && !editor.isDestroyed) editor.commands.setContent(recovery.content, { emitUpdate: false });
    snapshot.current = { title: recovery.title, meta: recovery.meta, content: recovery.content };
    setTitleState(recovery.title);
    setMetaState(recovery.meta);
    autosave.setVersion(recovery.baseVersion);
    setRecovery(null);
    autosave.markDirty();
  };
  const discardDraft = () => {
    setRecovery(null);
    void drafts.delete(doc.id);
  };

  const takeOver = async () => {
    const holder = lock.state.status === "blocked" ? lock.state.lock.holder.name : "the other editor";
    const ok = await confirm({
      title: `Take over editing from ${holder}?`,
      body: "There has been no activity for more than 90 seconds. If they come back, their unsaved changes stay on their device and cannot be saved over yours.",
      confirmLabel: "Take over",
    });
    if (ok) resume(await acquire(true));
  };
  // After the lock is ours again: clear a "locked" block and save what is pending.
  const resume = (next: LockState) => {
    if (next.status !== "held") return;
    autosave.unblock();
    void autosave.flush();
  };
  const editAgain = async () => resume(await acquire());

  // While someone else holds the lock, try again at the heartbeat cadence: a freed lock is taken and editing resumes,
  // a lock gone stale offers Take over. The tries are quiet (the notice stays), skip while another acquire is in
  // flight, and stop on leaving or unmounting.
  const blocked = lock.state.status === "blocked";
  const resumeRef = useRef(resume);
  resumeRef.current = resume;
  useEffect(() => {
    if (!blocked) return;
    const timer = setInterval(() => {
      void acquire(false, { quiet: true }).then((next) => resumeRef.current(next));
    }, HEARTBEAT_MS);
    return () => clearInterval(timer);
  }, [blocked, acquire]);

  const leaving = useRef(false);
  const leaveGuard = async (): Promise<boolean> => {
    writeDraftNow();
    await autosave.flush();
    if (!autosave.pending()) return true;
    return confirm({
      title: "Leave with unsaved changes?",
      body: "The latest changes could not reach the server. They stay on this device and are offered again when you reopen the document.",
      confirmLabel: "Leave",
      danger: true,
    });
  };
  useBlocker({
    shouldBlockFn: async () => (leaving.current ? false : !(await leaveGuard())),
    enableBeforeUnload: () => autosave.pending(),
  });

  const done = async () => {
    if (!(await leaveGuard())) return;
    leaving.current = true;
    await lock.release();
    void queryClient.invalidateQueries({ queryKey: ["doc"] });
    void queryClient.invalidateQueries({ queryKey: ["tree"] });
    onDone();
  };

  const notice: LockState = lockLost ? { status: "lost" } : lock.state;
  const notices = (
    <>
      <LockNotice state={notice} onTakeOver={() => void takeOver()} onRetry={() => void editAgain()} />
      <RecoveryPrompt draft={recovery} serverVersion={doc.version} onRestore={restoreDraft} onDiscard={discardDraft} />
      <ConflictDialog
        theirs={conflict}
        busy={busy}
        onKeepMine={() => void keepMine()}
        onTakeTheirs={takeTheirs}
        onCopyMine={() => void copyMine()}
        onRetry={() => setConflict(null)}
      />
      {confirmDialog}
      {dialogs}
    </>
  );
  const canvas = (
    <ThemeCanvas tokens={tokens} fileUrl={host.attachmentUrl} doc={{ kind: doc.kind, title, meta }}>
      <KlarityEditor
        key={mode}
        content={snapshot.current.content}
        mode={mode}
        host={host}
        onReady={(e) => setMade({ editor: e, mode })}
        onChange={(e) => {
          snapshot.current = { ...snapshot.current, content: e.getJSON() as TiptapDoc };
          autosave.markDirty();
        }}
        onBlur={() => void autosave.flush()}
      />
    </ThemeCanvas>
  );

  return (
    <>
      {children({
        title,
        setTitle,
        meta,
        setMeta,
        status: lockLost && autosave.pending() ? "locked" : autosave.status,
        error: autosave.error,
        lastSavedAt: autosave.lastSavedAt,
        editor: mode === "edit" && made?.mode === "edit" && editable && editor && !editor.isDestroyed ? editor : null,
        editable,
        notices,
        canvas,
        snapshot: () => snapshot.current,
        pending: autosave.pending,
        flush: autosave.flush,
        done,
      })}
    </>
  );
}
